// On-screen virtual stick + throttle buttons for touch devices.
// Replaces the earlier device-orientation tilt input.

// モバイル端末(スマホ/タブレット)か判定。OS/UA を主判定にし、タッチ・小画面で補完。
export function isTouchDevice() {
  if (typeof navigator !== 'undefined') {
    // 1) UA Client Hints の明示的な mobile フラグ
    if (navigator.userAgentData?.mobile === true) return true;
    const ua = navigator.userAgent || '';
    // 2) UA 文字列でスマホ/タブレット OS を判定
    if (/Android|iPhone|iPod|iPad|Mobile|Windows Phone|webOS|BlackBerry|IEMobile|Opera Mini|Kindle|Silk/i.test(ua)) return true;
    // 3) iPadOS 13+ は Mac を偽装するが maxTouchPoints>1 で判別
    if (/Mac/.test(ua) && (navigator.maxTouchPoints ?? 0) > 1) return true;
    // 4) タッチ能力
    if ((navigator.maxTouchPoints ?? 0) > 0 || (navigator.msMaxTouchPoints ?? 0) > 0) return true;
  }
  if (typeof window === 'undefined') return false;
  if ('ontouchstart' in window) return true;
  const mm = window.matchMedia?.bind(window);
  if (mm?.('(any-pointer: coarse)')?.matches) return true;
  // 5) フォールバック: スマホ相当の小画面
  if (mm?.('(max-width: 820px)')?.matches) return true;
  return (window.innerWidth || 9999) <= 820;
}

function getOverlay() {
  return document.getElementById('tilt-overlay');
}

export function hideOverlay() {
  const overlay = getOverlay();
  if (overlay) {
    overlay.hidden = true;
    overlay.innerHTML = '';
  }
}

function renderOverlay(html) {
  const overlay = getOverlay();
  overlay.innerHTML = html;
  overlay.hidden = false;
}

// Show "PC vs Mobile" mode picker. Resolves to 'pc' or 'mobile'.
// `onSelectSync` runs synchronously inside the click handler — needed for
// iOS Safari to count the click as a user gesture for AudioContext.resume().
export function chooseInputMode(defaultMode = 'pc', onSelectSync = null) {
  const isMobileDefault = defaultMode === 'mobile';
  renderOverlay(`
    <div class="tilt-card">
      <h2>操作モードを選択</h2>
      <p class="overlay-text">フライトの操縦方法を選んでください。</p>
      <div class="mode-buttons">
        <button class="mode-btn${isMobileDefault ? '' : ' recommended'}" data-mode="pc" type="button">
          <div class="mode-icon">⌨️</div>
          <div class="mode-title">PC モード</div>
          <div class="mode-desc">矢印キーで操縦<br>Shift / Ctrl で加減速</div>
        </button>
        <button class="mode-btn${isMobileDefault ? ' recommended' : ''}" data-mode="mobile" type="button">
          <div class="mode-icon">📱</div>
          <div class="mode-title">スマホモード</div>
          <div class="mode-desc">画面の仮想スティックで操縦<br>右下ボタンで加減速</div>
        </button>
      </div>
      <p class="tilt-hint">${
        isMobileDefault ? 'タッチ端末を検出 — スマホモード推奨' : 'PC を検出 — PC モード推奨'
      }</p>
    </div>
  `);

  return new Promise((resolve) => {
    getOverlay()
      .querySelectorAll('.mode-btn')
      .forEach((btn) => {
        btn.addEventListener(
          'click',
          () => {
            const mode = btn.dataset.mode;
            try { onSelectSync?.(mode); } catch (e) { console.error(e); }
            resolve(mode);
          },
          { once: true }
        );
      });
  });
}

function attachVirtualStick() {
  const wrap = document.getElementById('mobile-stick');
  if (!wrap) return { read: () => ({ yaw: 0, pitch: 0 }), setEnabled: () => {}, dispose: () => {} };
  wrap.hidden = false;

  const base = wrap.querySelector('.stick-base');
  const knob = wrap.querySelector('.stick-knob');
  const MAX_RADIUS = 50; // knob can travel up to 50px from center

  let yaw = 0;
  let pitch = 0;
  let activePointerId = null;

  function update(clientX, clientY) {
    const rect = base.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    let dx = clientX - cx;
    let dy = clientY - cy;
    const dist = Math.hypot(dx, dy);
    if (dist > MAX_RADIUS) {
      dx = (dx * MAX_RADIUS) / dist;
      dy = (dy * MAX_RADIUS) / dist;
    }
    knob.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
    // X drag: right = +1 yaw (turn right). Left = -1 yaw (turn left).
    yaw = dx / MAX_RADIUS;
    // Y drag: drag down = +1 pitch (nose down, matches ArrowDown convention).
    //         drag up   = -1 pitch (nose up,   matches ArrowUp).
    pitch = dy / MAX_RADIUS;
  }

  function reset() {
    knob.style.transform = 'translate(-50%, -50%)';
    yaw = 0;
    pitch = 0;
    activePointerId = null;
  }

  const onDown = (e) => {
    e.preventDefault();
    activePointerId = e.pointerId;
    try { base.setPointerCapture(e.pointerId); } catch {}
    update(e.clientX, e.clientY);
  };
  const onMove = (e) => {
    if (e.pointerId !== activePointerId) return;
    e.preventDefault();
    update(e.clientX, e.clientY);
  };
  const onUp = (e) => {
    if (e.pointerId !== activePointerId) return;
    e.preventDefault();
    try { base.releasePointerCapture(e.pointerId); } catch {}
    reset();
  };

  base.addEventListener('pointerdown', onDown);
  base.addEventListener('pointermove', onMove);
  base.addEventListener('pointerup', onUp);
  base.addEventListener('pointercancel', onUp);
  base.addEventListener('pointerleave', onUp);

  return {
    read: () => ({ yaw, pitch }),
    // ポーズ時に呼ぶ。ドラッグ途中でモーダルが開いても、setPointerCapture が
    // 残ってスティックが動き続けないよう capture を明示的に解放する。
    setEnabled(on) {
      if (on) return;
      if (activePointerId !== null) {
        try { base.releasePointerCapture(activePointerId); } catch {}
      }
      reset();
    },
    dispose() {
      base.removeEventListener('pointerdown', onDown);
      base.removeEventListener('pointermove', onMove);
      base.removeEventListener('pointerup', onUp);
      base.removeEventListener('pointercancel', onUp);
      base.removeEventListener('pointerleave', onUp);
      reset();
      wrap.hidden = true;
    },
  };
}

function attachThrottleButtons() {
  const wrap = document.getElementById('mobile-throttle');
  if (!wrap) return { read: () => 0, setEnabled: () => {}, dispose: () => {} };
  wrap.hidden = false;

  const downBtn = document.getElementById('throttle-down');
  const upBtn = document.getElementById('throttle-up');
  let thr = 0;

  const release = () => { thr = 0; };
  const bind = (el, value) => {
    const press = (e) => { e.preventDefault(); thr = value; };
    el.addEventListener('pointerdown', press);
    el.addEventListener('pointerup', release);
    el.addEventListener('pointerleave', release);
    el.addEventListener('pointercancel', release);
    return () => {
      el.removeEventListener('pointerdown', press);
      el.removeEventListener('pointerup', release);
      el.removeEventListener('pointerleave', release);
      el.removeEventListener('pointercancel', release);
    };
  };
  const cleanups = [bind(downBtn, -1), bind(upBtn, 1)];

  return {
    read: () => thr,
    setEnabled(on) { if (!on) thr = 0; },
    dispose() {
      cleanups.forEach((fn) => fn());
      wrap.hidden = true;
      thr = 0;
    },
  };
}

export async function createMobileInputProvider() {
  // No permission flow — the virtual stick is purely DOM/pointer-event driven.
  hideOverlay();

  const stick = attachVirtualStick();
  const throttle = attachThrottleButtons();

  return {
    read() {
      const { yaw, pitch } = stick.read();
      return { yaw, pitch, thr: throttle.read(), brake: 0 };
    },
    setEnabled(on) {
      stick.setEnabled(on);
      throttle.setEnabled(on);
    },
    dispose() {
      stick.dispose();
      throttle.dispose();
    },
  };
}
